import { domainData, DomainData, IDomainData } from "src/app/features/domain/models/domain-data";
import type { IAppData } from "../../app-services/app.factory";

export const domainProfile = {
    guid: 'guid',
    // Rauwe shard (guid-gekeyed foldernaam), NIET al gejoind met ConfigService.rootPublic$$/
    // logos_folderName$$: die twee laden asynchroon (config.json), onafhankelijk van en niet per se
    // vóór deze profieldata (zie DomainProfileService's constructor-fetch, die al vroeg via de
    // DI-graaf op gang komt — vaak vóór de app-initializer's configService.loadConfig() klaar is).
    // Was hier ooit wél al gejoind tot een kant-en-klare URL — een one-time snapshot die bij een
    // lege cache (dus een échte race tussen twee netwerkfetches) permanent logos_folderPath=null
    // kon bakken als /api/Domain(-default) toevallig eerder terugkwam dan /assets/config.json,
    // zonder ooit te herstellen (Title/SubTitle, die ConfigService niet nodig hebben, kwamen dan
    // wél goed door — precies het gerapporteerde symptoom). Nu rauw bewaard en pas in
    // DomainProfileService reactief gejoind (zelfde patroon als domainLogoThumb_url hieronder al
    // deed), zodat een latere config-load het gewoon corrigeert i.p.v. nooit meer bijtrekt.
    logos_fileShardPath: 'logos_fileShardPath',
    logo_version: 'logo_version',
    logoThumb_version: 'logoThumb_version',
    logoEmail_version: 'logoEmail_version',
    logoIconPng_version: 'logoIconPng_version',
    logoIconSvg_version: 'logoIconSvg_version',
    title_sub: 'subTitle',
    title: 'title'
} as const;

export type TDomainProfile = typeof domainProfile[keyof typeof domainProfile];

export interface IDomainProfile {
    [domainProfile.guid]?: string | null;
    [domainProfile.logos_fileShardPath]?: string | null;
    [domainProfile.logo_version]?: string | null;
    [domainProfile.logoThumb_version]?: string | null;
    [domainProfile.logoEmail_version]?: string | null;
    [domainProfile.logoIconPng_version]?: string | null;
    [domainProfile.logoIconSvg_version]?: string | null;
    [domainProfile.title_sub]?: string | null;
    [domainProfile.title]: string;
}

export class DomainProfile {
    [domainProfile.guid]?: string | null;
    [domainProfile.logos_fileShardPath]?: string | null;
    [domainProfile.logo_version]!: string | null;
    [domainProfile.logoThumb_version]!: string | null;
    [domainProfile.logoEmail_version]!: string | null;
    [domainProfile.logoIconPng_version]!: string | null;
    [domainProfile.logoIconSvg_version]!: string | null;
    [domainProfile.title_sub]?: string | null;
    [domainProfile.title]!: string | null;

    private constructor(data: IDomainData | IAppData) {
        let __guid: string | null = null;
        let __logosShardPath: string | null = null;
        let __title: string | null = null;
        let __title_sub: string | null = null;
        let __logoVersion: string | null = null;
        let __logoThumbVersion: string | null = null;
        let __logoEmailVersion: string | null = null;
        let __logoIconPngVersion: string | null = null;
        let __logoIconSvgVersion: string | null = null;

        if (Object.keys(data).includes(domainData.logos_fileShardPath)) {
            const __data = data as DomainData;
            __guid = __data[domainData.guid] ?? null;
            __logosShardPath = __data[domainData.logos_fileShardPath];
            __title = __data[domainData.name];
            __title_sub = __data[domainData.subTitle];
            // FolderShard staat altijd vast (guid-gekeyed, zie DomainDto.cs) en de publieke
            // bestandsnaam per variant ook (LogoService._PublicFileName) — de URL-string
            // alléén verandert dus nooit vanzelf bij upload/delete. Zonder een écht per-variant
            // versienummer hierin zou elke fresh page load (F5) terugvallen op de kale,
            // ongeversioneerde URL — precies de URL die dit domein al zijn hele geschiedenis
            // gebruikt, en dus een populair doelwit voor jarenoude browsercache (geen
            // Cache-Control op deze publieke bestanden, dus browsers cachen heuristisch op
            // Last-Modified). `null` hier betekent: variant bestaat niet → geen URL bouwen.
            //
            // Versie = modifiedOn, met guid als terugval: de globale audit-interceptor
            // (DbContext_Tables.ApplyAuditInformation) zet ModifiedOn alléén bij een Modified
            // save, nooit bij Added — een gloednieuwe variant (allereerste upload, nog nooit
            // vervangen) heeft dus modifiedOn=null terwijl hij wél bestaat. guid is wél altijd
            // gezet (ook bij insert) en blijft stabiel bij een replace (zelfde slot, nieuwe
            // bytes) — dus modifiedOn ?? guid dekt zowel "bestaat, nog nooit vervangen" (guid,
            // uniek genoeg om de historisch-kale URL nooit te hergebruiken) als "bestaat,
            // vervangen" (modifiedOn, verandert bij elke vervanging — noodzakelijk, want guid
            // verandert dan juist NIET).
            __logoVersion = __data[domainData.logo_modifiedOn] ?? __data[domainData.logo_guid];
            __logoThumbVersion = __data[domainData.logoThumb_modifiedOn] ?? __data[domainData.logoThumb_guid];
            __logoEmailVersion = __data[domainData.logoEmail_modifiedOn] ?? __data[domainData.logoEmail_guid];
            __logoIconPngVersion = __data[domainData.logoIconPng_modifiedOn] ?? __data[domainData.logoIconPng_guid];
            __logoIconSvgVersion = __data[domainData.logoIconSvg_modifiedOn] ?? __data[domainData.logoIconSvg_guid];
        } else {
            // Laatste-redmiddel (AppService): geen domain-0-guid meer hardcoderen — dat was een
            // verouderde, losse registratie naast de echte SSOT (/api/Domain/default via
            // defaultDomainProfileData$$). Zonder guid blijft hier alleen de app-titel over.
            const __data = data as IAppData;
            __title = __data.title$$();
        }

        this[domainProfile.guid] = __guid;
        this[domainProfile.logos_fileShardPath] = __logosShardPath;
        this[domainProfile.logo_version] = __logoVersion;
        this[domainProfile.logoThumb_version] = __logoThumbVersion;
        this[domainProfile.logoEmail_version] = __logoEmailVersion;
        this[domainProfile.logoIconPng_version] = __logoIconPngVersion;
        this[domainProfile.logoIconSvg_version] = __logoIconSvgVersion;
        this[domainProfile.title] = __title;
        this[domainProfile.title_sub] = __title_sub;
    }

    static create(data: IDomainData | IAppData | null): DomainProfile | null {
        return !!data ? new DomainProfile(data) : null;
    }
}
